import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import FileUploader from '../components/FileUploader';
import ProgressMonitor from '../components/ProgressMonitor';
import ContentEditorModal from '../components/ContentEditorModal';
import ShareModal from '../components/ShareModal';
import PromptsEditor from '../components/PromptsEditor';
import { api } from '../services/api';
import ModelSelector from '../components/ModelSelector';

interface CFPage {
  id: number;
  job_id: number;
  title: string;
  slug: string;
  generator_type: string;
  meta_title: string | null;
  meta_description: string | null;
  sections: Record<string, any>;
  acf_fields: Record<string, any> | null;
  word_count: number | null;
  qa_score: number | null;
  qa_passed: boolean;
  qa_errors: string[] | null;
  qa_warnings: string[] | null;
  status: string;
  is_edited: boolean;
  created_at: string | null;
  edited_at: string | null;
  edited_by_name: string | null;
  generated_at: string | null;
}

interface CFQASummary {
  total_pages: number;
  qa_passed: number;
  qa_failed: number;
  average_score: number;
}

interface CFResults {
  job: any;
  pages: CFPage[];
  total_pages: number;
  qa_summary?: CFQASummary;
}

interface RecentJob {
  id: number;
  project_id: number;
  status: string;
  completed_at: string | null;
  created_at: string;
  settings: Record<string, any> | null;
}

const GENERATOR_TYPES = [
  { id: 'program', name: 'Program Pages', description: 'Treatment program pages', icon: '📋' },
  { id: 'condition', name: 'Condition Pages', description: 'Condition treatment pages', icon: '🏥' },
  { id: 'sap', name: 'Service Area Pages', description: 'Service area pages', icon: '📍' },
];

type InputMode = 'project' | 'file' | 'text';

interface FactSheet {
  job_id: number;
  label: string;
  completed_at: string | null;
  word_count: number | null;
  has_content: boolean;
  step_name: string | null;
}

export default function ContentFrameworkV2Page() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  // Support both new URL structure (/projects/:projectId/agent) and legacy (?project_id=...)
  const projectId = urlProjectId || searchParams.get('project_id');

  const [step, setStep] = useState<'upload' | 'configure' | 'processing' | 'results'>('upload');
  const [jobId, setJobId] = useState<number | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(['program']);
  const [selectedModel, setSelectedModel] = useState<{ provider: string; model: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Input mode state
  const [inputMode, setInputMode] = useState<InputMode>('project');
  const [factsContent, setFactsContent] = useState('');
  const [titlesContent, setTitlesContent] = useState('');
  const [slugsContent, setSlugsContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Project facts selection state
  const [factSheets, setFactSheets] = useState<FactSheet[]>([]);
  const [selectedFactSheet, setSelectedFactSheet] = useState<number | null>(null);
  const [loadingFactSheets, setLoadingFactSheets] = useState(false);
  const [projectTitles, setProjectTitles] = useState('');
  const [projectSlugs, setProjectSlugs] = useState('');

  // Results state
  const [results, setResults] = useState<CFResults | null>(null);
  const [recentJobs, setRecentJobs] = useState<RecentJob[]>([]);
  const [, setLoadingJobs] = useState(false);

  // Prompts editor state
  const [showPrompts, setShowPrompts] = useState(false);

  // Editor modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<CFPage | null>(null);

  // Blank page creation state
  const [blankPageModal, setBlankPageModal] = useState<{ open: boolean; type: string }>({ open: false, type: '' });
  const [blankPageTitle, setBlankPageTitle] = useState('');
  const [blankPageSlug, setBlankPageSlug] = useState('');
  const [creatingBlank, setCreatingBlank] = useState(false);

  // ACF Editor modal state
  const [acfEditorOpen, setAcfEditorOpen] = useState(false);
  const [acfEditingPage, setAcfEditingPage] = useState<CFPage | null>(null);
  const [acfFields, setAcfFields] = useState<Record<string, any>>({});
  const [availableAcfFields, setAvailableAcfFields] = useState<string[]>([]);
  const [acfLoading, setAcfLoading] = useState(false);
  const [acfSaving, setAcfSaving] = useState(false);

  // Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [sharePageId, setSharePageId] = useState(0);
  const [sharePageTitle, setSharePageTitle] = useState('');

  // CSV Conversion state
  const [convertingProgram, setConvertingProgram] = useState(false);
  const [convertingCondition, setConvertingCondition] = useState(false);
  const [convertingSap, setConvertingSap] = useState(false);
  const [conversionProgress, setConversionProgress] = useState<{
    type: string;
    progress: number;
    status: string;
    message: string;
    logs: string[];
    total_pages?: number;
    converted_pages?: number;
  } | null>(null);

  // Ref to track polling timeout for cleanup
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  // Cleanup polling on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  const urlJobId = searchParams.get('job_id');

  // Fetch fact sheets and recent jobs when project is selected
  useEffect(() => {
    if (projectId) {
      if (inputMode === 'project') {
        fetchFactSheets();
      }
      loadRecentJobs();
    }
  }, [projectId, inputMode]);

  // Auto-load specific job from URL
  useEffect(() => {
    if (urlJobId && projectId) {
      const id = parseInt(urlJobId);
      if (!isNaN(id)) {
        viewJob({ id } as RecentJob);
      }
    }
  }, [urlJobId, projectId]);

  const loadRecentJobs = async () => {
    if (!projectId) return;
    try {
      setLoadingJobs(true);
      const response = await api.get(`/projects/${projectId}/jobs?system_type=contentframeworkv2&status=completed,failed&limit=10`);
      setRecentJobs(response.data || []);
    } catch (err) {
      console.error('Failed to load recent jobs:', err);
    } finally {
      setLoadingJobs(false);
    }
  };

  const viewJob = async (job: RecentJob) => {
    try {
      setJobId(job.id);
      const response = await api.get(`/contentframeworkv2/results/${job.id}`);
      setResults(response.data);
      setStep('results');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load job results');
    }
  };

  const fetchFactSheets = async () => {
    if (!projectId) return;
    try {
      setLoadingFactSheets(true);
      const response = await api.get(`/factsheets/project/${projectId}/selector`);
      setFactSheets(response.data.fact_sheets || []);
    } catch (err) {
      console.error('Failed to fetch fact sheets:', err);
      setFactSheets([]);
    } finally {
      setLoadingFactSheets(false);
    }
  };

  const handleProjectFactsSubmit = async () => {
    if (!projectId || !selectedFactSheet) {
      setError('Please select a FactsAgent output to use.');
      return;
    }

    try {
      setError(null);
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('factsagent_job_id', selectedFactSheet.toString());
      if (projectTitles.trim()) {
        formData.append('titles', projectTitles);
      }
      if (projectSlugs.trim()) {
        formData.append('slugs', projectSlugs);
      }

      const response = await api.post('/contentframeworkv2/upload-from-factsagent', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const newJobId = response.data.job_id;
      if (newJobId) {
        setJobId(newJobId);
        setStep('configure');
      } else {
        const match = response.data.message?.match(/Job ID:\s*(\d+)/);
        const parsedJobId = match ? parseInt(match[1]) : null;
        if (parsedJobId) {
          setJobId(parsedJobId);
          setStep('configure');
        } else {
          setError('Failed to get job ID from response');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load facts from project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!projectId) {
      setError('No project ID provided. Please select a project first.');
      return;
    }

    try {
      setError(null);
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append('facts_file', file);
      formData.append('project_id', projectId);

      const response = await api.post('/contentframeworkv2/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setJobId(response.data.job_id);
      setStep('configure');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload files');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTextSubmit = async () => {
    if (!projectId) {
      setError('No project ID provided. Please select a project first.');
      return;
    }

    if (!factsContent.trim()) {
      setError('Please enter the facts content.');
      return;
    }

    if (!titlesContent.trim()) {
      setError('Please enter the titles content.');
      return;
    }

    if (!slugsContent.trim()) {
      setError('Please enter the slugs content.');
      return;
    }

    try {
      setError(null);
      setIsSubmitting(true);

      const factsBlob = new Blob([factsContent], { type: 'text/plain' });
      const factsFile = new File([factsBlob], 'facts.txt', { type: 'text/plain' });
      const titlesBlob = new Blob([titlesContent], { type: 'text/plain' });
      const titlesFile = new File([titlesBlob], 'titles.txt', { type: 'text/plain' });
      const slugsBlob = new Blob([slugsContent], { type: 'text/plain' });
      const slugsFile = new File([slugsBlob], 'slugs.txt', { type: 'text/plain' });

      const formData = new FormData();
      formData.append('facts_file', factsFile);
      formData.append('titles_file', titlesFile);
      formData.append('slugs_file', slugsFile);
      formData.append('project_id', projectId);

      const response = await api.post('/contentframeworkv2/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setJobId(response.data.job_id);
      setStep('configure');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit content');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerate = async () => {
    if (!jobId) return;
    try {
      setError(null);
      await api.post(`/contentframeworkv2/generate/${jobId}`, {
        generator_types: selectedTypes,
        enable_enrichment: true
      });
      setStep('processing');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to start generation');
    }
  };

  const toggleType = (typeId: string) => {
    setSelectedTypes(prev =>
      prev.includes(typeId)
        ? prev.filter(id => id !== typeId)
        : [...prev, typeId]
    );
  };

  const handleReset = () => {
    setStep('upload');
    setJobId(null);
    setSelectedTypes(['program']);
    setError(null);
    setFactsContent('');
    setTitlesContent('');
    setSlugsContent('');
    setInputMode('project');
    setSelectedFactSheet(null);
    setProjectTitles('');
    setProjectSlugs('');
    setResults(null);
    loadRecentJobs();
  };

  const handleDelete = async () => {
    if (!jobId) return;
    if (!confirm('Are you sure you want to delete this job?')) return;
    try {
      await api.delete(`/contentframeworkv2/${jobId}`);
      handleReset();
    } catch (err: any) {
      alert('Failed to delete job: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openEditor = (page: CFPage) => {
    setEditingPage(page);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingPage(null);
  };

  const handleEditorSave = async () => {
    if (jobId) {
      try {
        const response = await api.get(`/contentframeworkv2/results/${jobId}`);
        setResults(response.data);
      } catch (err) {
        console.error('Failed to refresh results:', err);
      }
    }
  };

  const openBlankPageModal = (type: string) => {
    setBlankPageModal({ open: true, type });
    setBlankPageTitle('');
    setBlankPageSlug('');
  };

  const createBlankPage = async () => {
    if (!jobId || !blankPageTitle.trim()) return;
    setCreatingBlank(true);
    try {
      const formData = new FormData();
      formData.append('title', blankPageTitle.trim());
      formData.append('slug', blankPageSlug.trim());
      formData.append('generator_type', blankPageModal.type);
      await api.post(`/cppages/${jobId}/pages/create-blank`, formData);
      setBlankPageModal({ open: false, type: '' });
      // Refresh results
      const response = await api.get(`/contentframeworkv2/results/${jobId}`);
      setResults(response.data);
    } catch (err: any) {
      alert('Failed to create page: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCreatingBlank(false);
    }
  };

  const handlePageDelete = async (pageId: number, pageTitle: string) => {
    if (!confirm(`Delete "${pageTitle}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/contentframeworkv2/pages/${pageId}`);
      // Refresh pages
      if (jobId) {
        const response = await api.get(`/contentframeworkv2/results/${jobId}`);
        setResults(response.data);
      }
    } catch (err: any) {
      alert('Failed to delete page: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handlePageRewrite = async (pageId: number) => {
    try {
      await api.post(`/cppages/pages/${pageId}/regenerate`);
      closeEditor();
      // Start polling for results
      if (jobId) {
        const pollInterval = setInterval(async () => {
          try {
            const response = await api.get(`/contentframeworkv2/results/${jobId}`);
            setResults(response.data);
            const job = await api.get(`/contentframeworkv2/status/${jobId}`);
            if (job.data.status !== 'processing') {
              clearInterval(pollInterval);
            }
          } catch { clearInterval(pollInterval); }
        }, 5000);
        // Clear after 5 minutes max
        setTimeout(() => clearInterval(pollInterval), 300000);
      }
    } catch (err: any) {
      alert('Rewrite failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  // ACF Editor Functions
  const openAcfEditor = async (page: CFPage) => {
    setAcfEditingPage(page);
    setAcfLoading(true);
    setAcfEditorOpen(true);
    try {
      const response = await api.get(`/cppages/pages/${page.id}/acf-fields`);
      setAcfFields(response.data.acf_fields || {});
      setAvailableAcfFields(response.data.available_fields || []);
    } catch (err) {
      console.error('Failed to load ACF fields:', err);
      setError('Failed to load ACF fields');
    } finally {
      setAcfLoading(false);
    }
  };

  const closeAcfEditor = () => {
    setAcfEditorOpen(false);
    setAcfEditingPage(null);
    setAcfFields({});
    setAvailableAcfFields([]);
  };

  const handleAcfFieldChange = (fieldName: string, value: string) => {
    setAcfFields(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleAcfSave = async () => {
    if (!acfEditingPage) return;
    setAcfSaving(true);
    try {
      await api.put(`/cppages/pages/${acfEditingPage.id}/acf-fields`, {
        acf_fields: acfFields,
        edited_by_name: 'User'
      });
      if (jobId) {
        const response = await api.get(`/contentframeworkv2/results/${jobId}`);
        setResults(response.data);
      }
      closeAcfEditor();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save ACF fields');
    } finally {
      setAcfSaving(false);
    }
  };

  // Convert pages to ACF fields
  const convertToAcf = async (type: 'program' | 'condition' | 'sap') => {
    if (!jobId) return;
    const setConverting = type === 'program' ? setConvertingProgram : type === 'condition' ? setConvertingCondition : setConvertingSap;
    setConverting(true);
    setConversionProgress(null);
    setError(null);

    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    try {
      const response = await api.post(`/cppages/${jobId}/convert-all-to-acf?generator_type=${type}`);
      const { conversion_job_id } = response.data;
      if (!isMountedRef.current) return;

      const pollStatus = async () => {
        if (!isMountedRef.current) return;
        try {
          const statusResponse = await api.get(`/cppages/acf-conversion/${conversion_job_id}/status`);
          const status = statusResponse.data;
          if (!isMountedRef.current) return;

          setConversionProgress({
            type,
            progress: status.progress,
            status: status.status,
            message: status.recent_logs?.[status.recent_logs.length - 1] || 'Processing...',
            logs: status.recent_logs || [],
            total_pages: status.total_pages,
            converted_pages: status.converted_pages
          });

          if (status.status === 'completed' || status.status === 'failed') {
            setConverting(false);
            pollingTimeoutRef.current = null;
            if (isMountedRef.current) {
              const resultsResponse = await api.get(`/contentframeworkv2/results/${jobId}`);
              if (isMountedRef.current) setResults(resultsResponse.data);
            }
            if (status.status === 'failed' && isMountedRef.current) {
              setError(status.error_message || `Failed to convert ${type} pages`);
            }
          } else {
            if (isMountedRef.current) {
              pollingTimeoutRef.current = setTimeout(pollStatus, 2000);
            }
          }
        } catch (err: any) {
          console.error('Error polling status:', err);
          if (isMountedRef.current) {
            setConverting(false);
            setError('Failed to get conversion status');
          }
          pollingTimeoutRef.current = null;
        }
      };

      pollingTimeoutRef.current = setTimeout(pollStatus, 1000);
    } catch (err: any) {
      if (isMountedRef.current) {
        setError(err.response?.data?.detail || `Failed to start ${type} ACF conversion`);
        setConverting(false);
      }
    }
  };

  // Check if pages have ACF fields already
  const programPagesWithAcf = results?.pages.filter(p =>
    ['program', 'program_csv'].includes(p.generator_type) && p.acf_fields
  ).length || 0;
  const conditionPagesWithAcf = results?.pages.filter(p =>
    p.generator_type === 'condition' && p.acf_fields
  ).length || 0;
  const totalProgramPages = results?.pages.filter(p =>
    ['program', 'program_csv'].includes(p.generator_type)
  ).length || 0;
  const totalConditionPages = results?.pages.filter(p =>
    p.generator_type === 'condition'
  ).length || 0;
  const sapPagesWithAcf = results?.pages.filter(p =>
    p.generator_type === 'sap' && p.acf_fields
  ).length || 0;
  const totalSapPages = results?.pages.filter(p =>
    p.generator_type === 'sap'
  ).length || 0;

  const hasProgramPages = results?.pages.some(p => ['program', 'program_csv'].includes(p.generator_type)) || false;
  const hasConditionPages = results?.pages.some(p => p.generator_type === 'condition') || false;
  const hasSapPages = results?.pages.some(p => p.generator_type === 'sap') || false;

  // Group pages by generator type
  const pagesByType = results?.pages.reduce((acc, page) => {
    const type = page.generator_type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(page);
    return acc;
  }, {} as Record<string, CFPage[]>) || {};

  const canSubmitText = factsContent.trim() && titlesContent.trim() && slugsContent.trim();
  const canSubmitProject = selectedFactSheet !== null;

  return (
    <Layout projectId={projectId ? parseInt(projectId) : null}>
      <div className="animate-fade-in">
        {/* Page Header */}
        <div className="page-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">Content Framework v2</h1>
              <p className="page-subtitle">
                Upload Facts.txt and page titles to generate treatment center pages through 9 agents with a revision loop for higher-quality output
                <a href="https://drive.google.com/file/d/1eb6ZhnAg0tePiIlaqr1XkL33OgRas2iS/view" target="_blank" rel="noopener noreferrer" className="ml-3 inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg>
                  Video Tutorial
                </a>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowPrompts(!showPrompts)}
                className={showPrompts ? 'btn-primary' : 'btn-secondary'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {showPrompts ? 'Hide Prompts' : 'Edit Prompts'}
              </button>
              {step === 'results' && (
                <>
                  <button
                    onClick={() => window.open(`/api/contentframeworkv2/download/${jobId}?token=${localStorage.getItem('auth_token') || ''}`, '_blank')}
                    className="btn-primary"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download Results
                  </button>
                  <button onClick={handleReset} className="btn-secondary">
                    New Job
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-danger-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-danger-700">{error}</span>
          </div>
        )}

        {/* Prompts Editor */}
        {showPrompts && (
          <div className="mb-8">
            <PromptsEditor
              systemType="contentframeworkv2"
              title="Content Framework v2 Prompts"
              subtitle="Edit the system prompts used by each agent in the 9-agent pipeline."
              categories={{
                order: ['pipeline', 'brief', 'structure', 'skills'],
                names: { pipeline: 'Pipeline Agents', brief: 'Page Briefs', structure: 'Structure Rules', skills: 'Skills' },
                icons: { pipeline: '🔗', brief: '📋', structure: '🏗️', skills: '⚡' },
              }}
              onClose={() => setShowPrompts(false)}
            />
          </div>
        )}

        {/* Recent Jobs Section */}
        {recentJobs.length > 0 && step === 'upload' && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Content Framework v2 Jobs</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentJobs.map((job) => {
                const isFailed = job.status === 'failed';
                const completedTypes = (job.settings?.completed_types as string[]) || [];
                const failedTypes = (job.settings?.failed_types as string[]) || [];
                return (
                  <div
                    key={job.id}
                    onClick={() => viewJob(job)}
                    className={`card hover:shadow-lg transition-all cursor-pointer group ${
                      isFailed ? 'hover:border-amber-300 border-amber-200' : 'hover:border-brand-200'
                    }`}
                  >
                    <div className="card-body">
                      <div className="flex items-start space-x-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          isFailed
                            ? 'bg-gradient-to-br from-amber-400 to-amber-600'
                            : 'bg-gradient-to-br from-success-400 to-success-600'
                        }`}>
                          {isFailed ? (
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                          ) : (
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="flex flex-wrap gap-1.5">
                              {(job.settings?.generators as string[] || []).map((g: string) => (
                                <span key={g} className="text-base font-bold text-gray-900 group-hover:text-brand-600 transition-colors">
                                  {g === 'sap' ? 'SAP Pages' : g === 'program' ? 'Program Pages' : g === 'condition' ? 'Condition Pages' : g}
                                </span>
                              ))}
                              {!(job.settings?.generators as string[] || []).length && (
                                <span className="text-base font-bold text-gray-900 group-hover:text-brand-600 transition-colors">Content Pages</span>
                              )}
                            </div>
                            <span className="text-xs text-gray-400">#{job.id}</span>
                            {isFailed && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                Partial
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1 flex items-center">
                            <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {new Date(job.completed_at || job.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <span className={`text-sm font-medium group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 ${
                          isFailed ? 'text-amber-600' : 'text-brand-600'
                        }`}>
                          {isFailed ? 'View Partial Results' : 'View/Edit Pages'}
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">Or create a new Content Framework v2 job below</p>
            </div>
          </div>
        )}

        {/* Upload Step */}
        {step === 'upload' && (
          <div className="max-w-3xl mx-auto">
            <div className="card">
              <div className="card-body">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Input Files</h2>
                    <p className="text-gray-500">Upload files or paste content for Facts, Titles, and Slugs</p>
                  </div>
                </div>

                {/* Input Mode Toggle */}
                <div className="flex items-center bg-gray-100 rounded-lg p-1 mb-6">
                  <button
                    onClick={() => setInputMode('project')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      inputMode === 'project'
                        ? 'bg-white shadow-sm text-gray-900'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    From Project
                  </button>
                  <button
                    onClick={() => setInputMode('file')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      inputMode === 'file'
                        ? 'bg-white shadow-sm text-gray-900'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Upload Files
                  </button>
                  <button
                    onClick={() => setInputMode('text')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      inputMode === 'text'
                        ? 'bg-white shadow-sm text-gray-900'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Paste Text
                  </button>
                </div>

                {/* Project Facts Selection Mode */}
                {inputMode === 'project' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <h3 className="font-semibold text-indigo-900 mb-2">Select FactsAgent Output</h3>
                      <p className="text-sm text-indigo-700">Choose a completed FactsAgent output from this project to use as the facts source.</p>
                    </div>

                    {loadingFactSheets ? (
                      <div className="flex items-center justify-center py-8">
                        <span className="spinner mr-2"></span>
                        <span className="text-gray-500">Loading fact sheets...</span>
                      </div>
                    ) : factSheets.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="font-medium">No FactsAgent outputs found</p>
                        <p className="text-sm mt-1">Run FactsAgent first to generate facts for this project.</p>
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className="label">Select Facts Source</label>
                          <div className="space-y-2">
                            {factSheets.map((sheet) => (
                              <div
                                key={sheet.job_id}
                                onClick={() => setSelectedFactSheet(sheet.job_id)}
                                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                  selectedFactSheet === sheet.job_id
                                    ? 'border-indigo-500 bg-indigo-50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                      selectedFactSheet === sheet.job_id
                                        ? 'border-indigo-500 bg-indigo-500'
                                        : 'border-gray-300'
                                    }`}>
                                      {selectedFactSheet === sheet.job_id && (
                                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900">{sheet.label}</p>
                                      <p className="text-sm text-gray-500">
                                        Job #{sheet.job_id}
                                        {sheet.word_count && ` - ${sheet.word_count.toLocaleString()} words`}
                                      </p>
                                    </div>
                                  </div>
                                  {sheet.completed_at && (
                                    <span className="text-xs text-gray-400">
                                      {new Date(sheet.completed_at).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="label">
                            Titles <span className="text-gray-400 font-normal">(optional)</span>
                          </label>
                          <textarea
                            value={projectTitles}
                            onChange={(e) => setProjectTitles(e.target.value)}
                            placeholder="Enter page titles (one per line)..."
                            className="textarea h-24 font-mono text-sm"
                            disabled={isSubmitting}
                          />
                        </div>

                        <div>
                          <label className="label">
                            Slugs <span className="text-gray-400 font-normal">(optional)</span>
                          </label>
                          <textarea
                            value={projectSlugs}
                            onChange={(e) => setProjectSlugs(e.target.value)}
                            placeholder="Enter URL slugs (one per line)..."
                            className="textarea h-24 font-mono text-sm"
                            disabled={isSubmitting}
                          />
                        </div>

                        <button
                          onClick={handleProjectFactsSubmit}
                          disabled={isSubmitting || !canSubmitProject}
                          className="btn-primary w-full mt-4"
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner"></span>
                              Loading Facts...
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                              </svg>
                              Continue to Configuration
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* File Upload Mode */}
                {inputMode === 'file' && (
                  <div>
                    <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <h3 className="font-semibold text-indigo-900 mb-2">Required Files:</h3>
                      <ul className="text-sm text-indigo-700 space-y-1.5 list-disc list-inside">
                        <li>Facts file (TXT or JSON format)</li>
                        <li>Titles file (TXT format)</li>
                        <li>Slugs file (TXT format)</li>
                      </ul>
                    </div>
                    <FileUploader
                      onFileSelect={handleFileUpload}
                      accept=".txt,.json"
                      maxSize={10 * 1024 * 1024}
                      disabled={isSubmitting}
                    />
                  </div>
                )}

                {/* Text Input Mode */}
                {inputMode === 'text' && (
                  <div className="space-y-4">
                    <div>
                      <label className="label">
                        Facts Content <span className="text-danger-500">*</span>
                      </label>
                      <textarea
                        value={factsContent}
                        onChange={(e) => setFactsContent(e.target.value)}
                        placeholder="Paste your facts content here..."
                        className="textarea h-40 font-mono text-sm"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label className="label">
                        Titles <span className="text-danger-500">*</span>
                      </label>
                      <textarea
                        value={titlesContent}
                        onChange={(e) => setTitlesContent(e.target.value)}
                        placeholder="Paste your page titles here (one per line)..."
                        className="textarea h-32 font-mono text-sm"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label className="label">
                        Slugs <span className="text-danger-500">*</span>
                      </label>
                      <textarea
                        value={slugsContent}
                        onChange={(e) => setSlugsContent(e.target.value)}
                        placeholder="Paste your URL slugs here (one per line)..."
                        className="textarea h-32 font-mono text-sm"
                        disabled={isSubmitting}
                      />
                    </div>

                    <button
                      onClick={handleTextSubmit}
                      disabled={isSubmitting || !canSubmitText}
                      className="btn-accent w-full mt-4"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner"></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                          Continue to Configuration
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Configure Step */}
        {step === 'configure' && (
          <div className="max-w-3xl mx-auto">
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900">Select Page Types</h2>
                <p className="text-sm text-gray-500 mt-1">Choose which types of pages to generate via the 9-agent pipeline</p>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {GENERATOR_TYPES.map((type) => (
                    <div
                      key={type.id}
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        selectedTypes.includes(type.id)
                          ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => toggleType(type.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-xl">
                          {type.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-gray-900">{type.name}</h3>
                            <input
                              type="checkbox"
                              checked={selectedTypes.includes(type.id)}
                              onChange={() => {}}
                              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                            />
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{type.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pipeline Info */}
                <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <h4 className="font-semibold text-indigo-900 mb-2">9-Agent Pipeline</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-indigo-700">
                    <span className="bg-white px-2 py-1 rounded">1. Strategist</span>
                    <span className="bg-white px-2 py-1 rounded">2. Semantic SEO</span>
                    <span className="bg-white px-2 py-1 rounded">3. Writer</span>
                    <span className="bg-white px-2 py-1 rounded">4. Self-Checker</span>
                    <span className="bg-white px-2 py-1 rounded">5. Fact & Compliance</span>
                    <span className="bg-white px-2 py-1 rounded">6. SEO Validator</span>
                    <span className="bg-white px-2 py-1 rounded">7. Conversion</span>
                    <span className="bg-white px-2 py-1 rounded">8. Master Reviewer</span>
                    <span className="bg-white px-2 py-1 rounded">9. Interviewer</span>
                  </div>
                </div>
              </div>
              <div className="card-footer flex justify-between items-center">
                <button onClick={() => setStep('upload')} className="btn-secondary">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                <div className="flex items-center gap-3">
                  <ModelSelector value={selectedModel} onChange={setSelectedModel} />
                  <button
                    onClick={handleGenerate}
                    disabled={selectedTypes.length === 0}
                    className="btn-primary"
                  >
                    Generate Pages
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Processing Step */}
        {step === 'processing' && jobId && (
          <div className="max-w-4xl mx-auto">
            <div className="card">
              <div className="card-body">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Running Content Framework v2 Pipeline</h2>
                  <p className="text-gray-500">Processing {selectedTypes.length} page type(s) through the 9-agent pipeline. This may take several minutes per page.</p>
                </div>
                <ProgressMonitor
                  jobId={jobId}
                  systemType="contentframeworkv2"
                  customSteps={selectedTypes.map(type => {
                    const gen = GENERATOR_TYPES.find(g => g.id === type);
                    return gen?.name || type;
                  })}
                  onStatusUpdate={async (status) => {
                    if (status.status === 'completed' || status.status === 'failed') {
                      try {
                        const response = await api.get(`/contentframeworkv2/results/${jobId}`);
                        setResults(response.data);
                        if (status.status === 'failed') {
                          setError(status.error_message || 'Generation partially failed');
                        }
                        setStep('results');
                      } catch (err) {
                        console.error('Failed to load results:', err);
                        setStep('results');
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Results Step */}
        {step === 'results' && jobId && results && (
          <div className="max-w-6xl mx-auto">
            {/* Results Header */}
            {(() => {
              const jobSettings = results.job.settings || {};
              const completedTypes: string[] = jobSettings.completed_types || [];
              const failedTypes: string[] = jobSettings.failed_types || [];
              const allGenerators: string[] = jobSettings.generators || [];
              const isPartial = results.job.status === 'failed' && failedTypes.length > 0;

              return isPartial ? (
                <div className="card mb-6">
                  <div className="card-header bg-amber-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-amber-900">Partially Completed</h2>
                          <p className="text-sm text-amber-700">
                            {completedTypes.length} of {allGenerators.length} types succeeded &mdash; {results.total_pages} pages saved
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            setError(null);
                            await api.post(`/contentframeworkv2/generate/${jobId}?resume=true`, {
                              generator_types: allGenerators,
                              enable_enrichment: true,
                            });
                            setSelectedTypes(failedTypes);
                            setStep('processing');
                          } catch (err: any) {
                            setError(err.response?.data?.detail || 'Failed to resume job');
                          }
                        }}
                        className="btn-primary"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Resume Failed Types
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {allGenerators.map((g: string) => {
                        const typeInfo = GENERATOR_TYPES.find(t => t.id === g);
                        const label = typeInfo?.name || g;
                        if (completedTypes.includes(g)) {
                          return (
                            <span key={g} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                              {label}
                            </span>
                          );
                        }
                        if (failedTypes.includes(g)) {
                          return (
                            <span key={g} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                              {label}
                            </span>
                          );
                        }
                        return (
                          <span key={g} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="card mb-6">
                  <div className="card-header bg-success-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-success-100 flex items-center justify-center">
                        <svg className="w-6 h-6 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-success-900">Generation Complete</h2>
                        <p className="text-sm text-success-700">
                          {results.total_pages} pages generated via Content Framework v2 pipeline
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Pages Grid by Type */}
            {Object.entries(pagesByType).map(([type, pages]) => {
              const typeInfo = GENERATOR_TYPES.find(t => t.id === type);
              return (
                <div key={type} className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">{typeInfo?.icon || '📄'}</span>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {typeInfo?.name || type} ({pages.length} pages)
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pages.map((page) => (
                      <div
                        key={page.id}
                        className="card hover:shadow-lg hover:border-brand-200 transition-all cursor-pointer group"
                        onClick={() => openEditor(page)}
                      >
                        <div className="card-body">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-gray-900 group-hover:text-brand-600 transition-colors truncate">
                                {page.title}
                              </h4>
                              {page.slug && (
                                <p className="text-xs text-gray-400 mt-1 truncate">/{page.slug}</p>
                              )}
                              {page.meta_description && (
                                <p className="text-xs text-gray-500 mt-2 line-clamp-2" title={page.meta_description}>
                                  {page.meta_description}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 flex-wrap">
                                {page.word_count && (
                                  <span>{page.word_count.toLocaleString()} words</span>
                                )}
                                {page.is_edited && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded font-medium bg-blue-100 text-blue-800">
                                    Edited
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col gap-1">
                              <a
                                href={`/api/cppages/pages/${page.id}/download?format=md&token=${localStorage.getItem('auth_token') || ''}`}
                                download
                                onClick={(e) => e.stopPropagation()}
                                className="btn-ghost btn-sm"
                                title="Download"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                              </a>
                            </div>
                          </div>
                          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                            <span className="text-brand-600 text-sm font-medium inline-flex items-center gap-1">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit Content
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openAcfEditor(page);
                                }}
                                className="btn-secondary btn-sm"
                                title="Edit ACF Fields"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                                </svg>
                                ACF
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditor(page);
                                }}
                                className="btn-primary btn-sm"
                              >
                                Edit
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSharePageId(page.id);
                                  setSharePageTitle(page.title || 'Untitled');
                                  setShareModalOpen(true);
                                }}
                                className="px-2.5 py-1 text-xs font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg"
                              >
                                Share
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePageDelete(page.id, page.title || 'Untitled');
                                }}
                                className="px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg"
                                title="Delete page"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* + Create Blank Page Card */}
                    <div
                      className="card border-2 border-dashed border-gray-300 hover:border-brand-400 hover:bg-brand-50 transition-all cursor-pointer group flex items-center justify-center min-h-[140px]"
                      onClick={() => openBlankPageModal(type)}
                    >
                      <div className="text-center py-6">
                        <svg className="w-8 h-8 mx-auto text-gray-400 group-hover:text-brand-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <p className="mt-2 text-sm font-medium text-gray-500 group-hover:text-brand-600">Create Blank Page</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* CSV Export Section */}
            <div className="card mb-6">
              <div className="card-header">
                <h3 className="font-semibold text-gray-900">Export to WP All Import CSV</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Convert content to ACF fields, then download CSV files formatted for WordPress All Import
                </p>
              </div>
              <div className="card-body space-y-4">
                {/* Conversion Progress */}
                {conversionProgress && (convertingProgram || convertingCondition || convertingSap) && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-800">
                        Converting {conversionProgress.type} pages to ACF fields...
                      </span>
                      <span className="text-sm text-blue-600">{conversionProgress.progress}%</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2 mb-3">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${conversionProgress.progress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-blue-700 mb-2">{conversionProgress.message}</p>
                    {conversionProgress.logs.length > 0 && (
                      <div className="max-h-32 overflow-y-auto bg-white rounded p-2 text-xs font-mono text-gray-600">
                        {conversionProgress.logs.slice(-5).map((log, idx) => (
                          <div key={idx} className="py-0.5">{log}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {conversionProgress && !convertingProgram && !convertingCondition && !convertingSap && conversionProgress.status === 'completed' && (
                  <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                    <p className="text-sm text-green-800">
                      <strong>{conversionProgress.type.charAt(0).toUpperCase() + conversionProgress.type.slice(1)} pages:</strong>{' '}
                      {conversionProgress.converted_pages} of {conversionProgress.total_pages} converted successfully
                    </p>
                  </div>
                )}

                {/* Program Pages Section */}
                {hasProgramPages && (
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">Program Pages</h4>
                        <p className="text-sm text-gray-500">
                          {programPagesWithAcf} of {totalProgramPages} pages converted to ACF fields
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => convertToAcf('program')}
                          disabled={convertingProgram}
                          className="btn-secondary"
                        >
                          {convertingProgram ? (
                            <><span className="spinner"></span> Converting...</>
                          ) : (
                            <>Convert to ACF</>
                          )}
                        </button>
                        <a
                          href={`/api/cppages/${jobId}/download-program-csv?token=${localStorage.getItem('auth_token') || ''}`}
                          download
                          className={programPagesWithAcf > 0
                            ? 'btn-primary ring-2 ring-green-400 ring-offset-2 animate-pulse hover:animate-none'
                            : 'btn-secondary opacity-50 cursor-not-allowed pointer-events-none'}
                          onClick={(e) => programPagesWithAcf === 0 && e.preventDefault()}
                        >
                          Download CSV
                        </a>
                      </div>
                    </div>
                    {programPagesWithAcf === 0 && (
                      <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                        Click "Convert to ACF" first to extract ACF fields from content before downloading CSV
                      </p>
                    )}
                  </div>
                )}

                {/* Condition Pages Section */}
                {hasConditionPages && (
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">Condition Pages</h4>
                        <p className="text-sm text-gray-500">
                          {conditionPagesWithAcf} of {totalConditionPages} pages converted to ACF fields
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => convertToAcf('condition')}
                          disabled={convertingCondition}
                          className="btn-secondary"
                        >
                          {convertingCondition ? (
                            <><span className="spinner"></span> Converting...</>
                          ) : (
                            <>Convert to ACF</>
                          )}
                        </button>
                        <a
                          href={`/api/cppages/${jobId}/download-condition-csv?token=${localStorage.getItem('auth_token') || ''}`}
                          download
                          className={conditionPagesWithAcf > 0
                            ? 'btn-accent ring-2 ring-green-400 ring-offset-2 animate-pulse hover:animate-none'
                            : 'btn-secondary opacity-50 cursor-not-allowed pointer-events-none'}
                          onClick={(e) => conditionPagesWithAcf === 0 && e.preventDefault()}
                        >
                          Download CSV
                        </a>
                      </div>
                    </div>
                    {conditionPagesWithAcf === 0 && (
                      <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                        Click "Convert to ACF" first to extract ACF fields from content before downloading CSV
                      </p>
                    )}
                  </div>
                )}

                {/* SAP Pages Section */}
                {hasSapPages && (
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">Service Area Pages (SAP)</h4>
                        <p className="text-sm text-gray-500">
                          {sapPagesWithAcf} of {totalSapPages} pages converted to ACF fields
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => convertToAcf('sap')}
                          disabled={convertingSap}
                          className="btn-secondary"
                        >
                          {convertingSap ? (
                            <><span className="spinner"></span> Converting...</>
                          ) : (
                            <>Convert to ACF</>
                          )}
                        </button>
                        <a
                          href={`/api/cppages/${jobId}/download-sap-csv?token=${localStorage.getItem('auth_token') || ''}`}
                          download
                          className={sapPagesWithAcf > 0
                            ? 'btn-primary ring-2 ring-green-400 ring-offset-2 animate-pulse hover:animate-none'
                            : 'btn-secondary opacity-50 cursor-not-allowed pointer-events-none'}
                          onClick={(e) => sapPagesWithAcf === 0 && e.preventDefault()}
                        >
                          Download CSV
                        </a>
                      </div>
                    </div>
                    {sapPagesWithAcf === 0 && (
                      <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                        Click "Convert to ACF" first to extract ACF fields from content before downloading CSV
                      </p>
                    )}
                  </div>
                )}

                {!hasProgramPages && !hasConditionPages && !hasSapPages && (
                  <p className="text-gray-500 text-sm">No pages available for CSV export</p>
                )}
              </div>
            </div>

            {/* Job Management */}
            <div className="card">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {(results.job.settings?.generators as string[] || []).map((g: string) => g === 'sap' ? 'SAP' : g.charAt(0).toUpperCase() + g.slice(1)).join(' + ') || 'Content'} Pages
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      #{results.job.id} | {results.total_pages} pages generated
                      {results.job.completed_at && ` | Completed: ${new Date(results.job.completed_at).toLocaleString()}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <a
                      href={`/api/contentframeworkv2/${jobId}/download-all?format=md&token=${localStorage.getItem('auth_token') || ''}`}
                      download
                      className="btn-primary"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download All (ZIP)
                    </a>
                    <button onClick={handleReset} className="btn-secondary">
                      New Job
                    </button>
                    <button onClick={handleDelete} className="btn-danger">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Job
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content Editor Modal */}
      {editorOpen && editingPage && (
        <ContentEditorModal
          contentId={editingPage.id}
          contentTitle={editingPage.title}
          contentType={editingPage.generator_type}
          apiEndpoint={`/contentframeworkv2/pages/${editingPage.id}/content`}
          isOpen={editorOpen}
          onClose={closeEditor}
          onSave={handleEditorSave}
          onRewrite={() => handlePageRewrite(editingPage.id)}
        />
      )}

      {/* ACF Fields Editor Modal */}
      {acfEditorOpen && acfEditingPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Edit ACF Fields</h2>
                <p className="text-sm text-gray-500">{acfEditingPage.title}</p>
              </div>
              <button
                onClick={closeAcfEditor}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {acfLoading ? (
                <div className="flex items-center justify-center py-12">
                  <span className="spinner mr-2"></span>
                  <span className="text-gray-500">Loading ACF fields...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>{availableAcfFields.length}</strong> ACF fields available for {acfEditingPage.generator_type} pages.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {availableAcfFields.map((fieldName) => (
                      <div key={fieldName} className="border border-gray-200 rounded-lg p-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {fieldName}
                        </label>
                        {(fieldName.includes('_content') || fieldName.includes('_intro') ||
                          fieldName.includes('_readmore') || fieldName.includes('_info') ||
                          fieldName === 'seo_desc' || fieldName.includes('_a')) ? (
                          <textarea
                            value={acfFields[fieldName] || ''}
                            onChange={(e) => handleAcfFieldChange(fieldName, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm font-mono"
                            rows={4}
                            placeholder={`Enter ${fieldName}...`}
                          />
                        ) : (
                          <input
                            type="text"
                            value={acfFields[fieldName] || ''}
                            onChange={(e) => handleAcfFieldChange(fieldName, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
                            placeholder={`Enter ${fieldName}...`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button onClick={closeAcfEditor} className="btn-secondary" disabled={acfSaving}>
                Cancel
              </button>
              <button
                onClick={handleAcfSave}
                disabled={acfLoading || acfSaving}
                className="btn-primary"
              >
                {acfSaving ? (
                  <><span className="spinner"></span> Saving...</>
                ) : (
                  <>Save ACF Fields</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Blank Page Modal */}
      {blankPageModal.open && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setBlankPageModal({ open: false, type: '' })} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Create Blank {blankPageModal.type === 'sap' ? 'SAP' : blankPageModal.type === 'program' ? 'Program' : 'Condition'} Page
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Create an empty page to add your own content manually
                </p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="label">Page Title *</label>
                  <input
                    type="text"
                    value={blankPageTitle}
                    onChange={(e) => {
                      setBlankPageTitle(e.target.value);
                      // Auto-generate slug from title
                      if (!blankPageSlug || blankPageSlug === blankPageTitle.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')) {
                        setBlankPageSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
                      }
                    }}
                    placeholder="e.g. Alcohol Addiction Treatment"
                    className="input"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="label">URL Slug</label>
                  <input
                    type="text"
                    value={blankPageSlug}
                    onChange={(e) => setBlankPageSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))}
                    placeholder="auto-generated-from-title"
                    className="input font-mono text-sm"
                  />
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setBlankPageModal({ open: false, type: '' })}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={createBlankPage}
                  disabled={creatingBlank || !blankPageTitle.trim()}
                  className="btn-primary"
                >
                  {creatingBlank ? (
                    <><span className="spinner"></span> Creating...</>
                  ) : (
                    <>Create Page</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        contentType="cppages"
        contentId={sharePageId}
        contentTitle={sharePageTitle}
      />
    </Layout>
  );
}
